import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, ShieldCheck, Info } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { usdt, LIMIT_LABELS, idemKey } from "../lib/usdt";
import { useApp } from "../context/AppContext";
import { MivoCard } from "../components/MivoCard";
import { PageHead } from "./Shop";

import { tx, useLang, txf } from "../lib/i18n";
import { PageLoading } from "../components/PageLoading";
const SandboxBanner = ({ label }) => { useLang(); return label ? (
  <div className="mb-6 rounded-xl border border-amber-300/40 bg-amber-300/5 px-4 py-3 text-xs text-amber-200" data-testid="sandbox-banner">
    {tx(label)} {tx("· Live USDT funding is disabled until the provider, custody, security and compliance approvals are complete.")}
  </div>
) : null; };

const LimitsTable = ({ tiers }) => { useLang(); return (
  <div className="overflow-x-auto rounded-2xl border border-[var(--mv-border)]" data-testid="tier-limits-table">
    <table className="w-full min-w-[720px] text-sm">
      <thead className="bg-[var(--mv-card)] text-left text-[11px] uppercase tracking-wider text-[var(--mv-dim)]">
        <tr><th className="p-3">{tx("Limit (USDT, not USD)")}</th>{tiers.map((t) => <th key={t.code} className="p-3">{tx(t.name)}</th>)}</tr>
      </thead>
      <tbody>
        {Object.entries(LIMIT_LABELS).map(([k, l]) => (
          <tr key={k} className="border-t border-[var(--mv-border)]">
            <td className="p-3 text-[var(--mv-muted)]">{tx(l)}</td>
            {tiers.map((t) => <td key={t.code} className="p-3" data-testid={`limit-${t.code}-${k}`}>{tx(t.limits ? usdt(t.limits.limits[k]) : "Not published")}</td>)}
          </tr>
        ))}
        <tr className="border-t border-[var(--mv-border)]"><td className="p-3 text-[var(--mv-muted)]">{tx("Limit status")}</td>
          {tiers.map((t) => <td key={t.code} className="p-3 text-[11px] text-amber-300">{tx(t.limits?.label)}{tx(t.scheduled_limits?.length ? txf("· change scheduled {0}", new Date(t.scheduled_limits[0].effective_from).toLocaleDateString()) : "")}</td>)}</tr>
      </tbody>
    </table>
  </div>
); };

const ELIG = { verified_buyer: "Free for verified buyers", returning: "Returning customers (1+ paid order) or approval", admin_approval: "Admin approval required", invite: "Invitation or Admin approval only" };

export function CardsExplore() { useLang();
  const { user, t: tr } = useApp();
  const [data, setData] = useState(null);
  const [elig, setElig] = useState(null);
  useEffect(() => { api.get("/cards/tiers").then((r) => setData(r.data)); }, []);
  useEffect(() => { if (user) api.get("/cards/eligibility").then((r) => setElig(r.data)).catch(() => {}); }, [user]);
  if (!data) return <div className="mv-container py-24 text-[var(--mv-muted)]"><PageLoading /></div>;
  return (
    <div className="mv-container" data-testid="cards-explore-page">
      <PageHead eyebrow={tx("MY MIVO CARD · EXPLORE CARDS")} title={tx("Claim it. Personalize it. Control it.")}>
        <p className="mt-3 max-w-2xl text-sm text-[var(--mv-muted)]">{tx("MIVO Shopping Cards are MIVO-only prepaid virtual cards backed by your single USDT account. They are not Visa, Mastercard, bank or credit cards and cannot be used outside MIVO.")}</p>
      </PageHead>
      <SandboxBanner label={tx(data.label)} />
      <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
        {data.tiers.map((t, i) => {
          const e = elig?.tiers?.[t.code];
          return (
            <div key={t.code} className="rounded-3xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-6" data-testid={`tier-card-${t.code}`}>
              <div className="mivo-card-float" style={{ animationDelay: `-${i * 1.7}s` }}>
                <MivoCard tier={t} name="YOUR NAME" numberText="MV-••••-••••-••••" validText="••/••••" balanceText="•••• USDT" codeText="••••" sandbox={data.mode === "sandbox"} testid={`tier-preview-${t.code}`} />
              </div>
              <div className="mt-5 flex items-center justify-between">
                <h3 className="text-xl font-semibold">{tx(t.name)} <span className="text-sm font-normal text-[var(--mv-dim)]">· {tx(t.subtitle)}</span></h3>
                {tx(e && <span className="rounded-full bg-[var(--mv-teal-dim)] px-2.5 py-1 text-[10px] text-[var(--mv-teal)]" data-testid={`tier-eligibility-${t.code}`}>{tx(e.label)}</span>)}
              </div>
              <p className="mt-1 text-xs text-[var(--mv-muted)]" data-testid={`tier-rule-${t.code}`}>{tx(t.eligibility_summary || ELIG[t.eligibility])}</p>
              <ul className="mt-3 space-y-1.5">{t.benefits.map((b) => <li key={b} className="flex gap-2 text-sm text-[var(--mv-muted)]"><Check className="mt-0.5 h-4 w-4 text-[var(--mv-teal)]" />{tx(b)}</li>)}</ul>
              <p className="mt-3 text-[11px] text-[var(--mv-dim)]">{tx(t.fee_note)}</p>
              <UpgradeTips e={e && { ...e, code: t.code }} />
              <ClaimButton t={t} e={e} />
            </div>
          );
        })}
      </div>
      <h2 className="mb-4 mt-16 text-lg md:text-xl font-medium">{tr("card.compare")}</h2>
      <LimitsTable tiers={data.tiers} />
      <p className="mt-3 text-xs text-[var(--mv-dim)]">{tx("Limits are set and approved by MIVO. Periods use UTC calendar days and months. Pending deposits are not spendable.")} <Link to="/cards/terms" className="text-[var(--mv-teal)]">{tx("Card Program terms")}</Link></p>
    </div>
  );
}

const UpgradeTips = ({ e }) => { useLang();
  if (!e?.tips?.length) return null;
  const say = (x) => (x.key === "verified_buyer" ? tx("Verify your email address") : x.key === "invite" ? tx("Receive an invitation from MIVO") : x.count === 1 ? tx("Complete 1 more paid order") : txf("Complete {0} more paid orders", x.count));
  return (
    <div className="mt-4 rounded-xl border border-[var(--mv-border-2)] bg-[var(--mv-bg)] p-3 text-xs" data-testid={`upgrade-tips-${e.code || "tier"}`}>
      <p className="font-semibold text-[var(--mv-teal)]">{tx(e.tips_mode === "any" ? "To qualify, do any one of these:" : "To qualify, you still need to:")}</p>
      <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-[var(--mv-muted)]">{e.tips.map((x) => <li key={x.key}>{say(x)}</li>)}</ul>
      <p className="mt-1.5 text-[10px] text-[var(--mv-dim)]">{tx("MIVO Admin still reviews every request.")}</p>
    </div>
  );
};

const ClaimButton = ({ t, e }) => { useLang();
  const { requireAuth, t: tr } = useApp();
  const nav = useNavigate();
  const st = e?.status;
  const label = st === "eligible" ? tx("Request this card") : st === "not_eligible" ? tx("Not eligible yet") : tr({ current: "card.current", pending: "card.pending", invite_only: "card.inviteOnly", upgrade: "card.upgrade", application: "card.apply" }[st] || "card.claim");
  const disabled = ["current", "pending", "invite_only", "not_eligible"].includes(st);
  const go = () => { if (!requireAuth()) { sessionStorage.setItem("mivo_return", `/cards/claim/${t.code}`); return; } nav(`/cards/claim/${t.code}`); };
  return <button disabled={disabled} onClick={go} className="mt-5 w-full mv-btn-primary rounded-xl py-3 text-sm disabled:opacity-40" data-testid={`claim-button-${t.code}`}>{tx(label)}</button>;
};

export function ClaimCard() { useLang();
  const { code } = useParams();
  const { user } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [elig, setElig] = useState(null);
  const [name, setName] = useState(user?.name || "");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const key = useMemo(idemKey, []);
  useEffect(() => { api.get("/cards/tiers").then((r) => setData(r.data)); api.get("/cards/eligibility").then((r) => setElig(r.data)); }, []);
  const t = data?.tiers.find((x) => x.code === code);
  if (!data || !elig) return <div className="mv-container py-24 text-[var(--mv-muted)]"><PageLoading /></div>;
  if (!t) return <div className="mv-container py-24">{tx("This card tier is not available.")}</div>;
  const e = elig.tiers[code];
  const clean = name.replace(/\s+/g, " ").trimStart();
  const valid = /^[\p{L}]+(?:[ .'-][\p{L}]+)*\.?$/u.test(clean.trim()) && clean.trim().length >= 2 && clean.trim().length <= 26;
  const submit = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/cards/claim", { tier_code: code, display_name: clean.trim(), agree, idempotency_key: key });
      if (r.application.status === "issued") { toast.success(tx("Your MIVO card is ready")); nav("/wallet"); }
      else { toast.success(tx("Application submitted for Admin approval")); nav("/wallet"); }
    } catch (err) { toast.error(tx(errMsg(err))); }
    setBusy(false);
  };
  return (
    <div className="mv-container" data-testid="claim-card-page">
      <PageHead eyebrow={txf("CLAIM · {0}", t.name.toUpperCase())} title={tx("Personalize your MIVO card")} />
      <SandboxBanner label={tx(data.label)} />
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="mivo-card-float"><MivoCard tier={t} name={clean || "YOUR NAME"} numberText="MV-••••-••••-••••" validText="••/••••" balanceText="•••• USDT" codeText="••••" sandbox={data.mode === "sandbox"} testid="claim-preview" /></div>
        <div className="space-y-5">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--mv-muted)]">{tx("Name to display on your card")}</span>
            <input value={name} maxLength={26} onChange={(ev) => setName(ev.target.value)} data-testid="claim-name-input"
              className="w-full rounded-xl border border-[var(--mv-border-2)] bg-[var(--mv-bg)] px-4 py-3 outline-none focus:border-[var(--mv-teal)]" />
            <span className={`mt-1 block text-xs ${valid || !name ? "text-[var(--mv-dim)]" : "text-red-400"}`} data-testid="claim-name-hint">
              {tx(valid || !name ? txf("{0}/26 · letters, spaces, apostrophes, hyphens. Does not change your verified identity.", clean.trim().length) : "Use 2–26 letters, spaces, apostrophes, hyphens or periods")}
            </span>
          </label>
          <div className="rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-4">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-[var(--mv-teal)]" /> {tx("Effective limits (")}{tx(t.limits?.label)})</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {Object.entries(LIMIT_LABELS).map(([k, l]) => <div key={k} className="flex justify-between border-b border-[var(--mv-border)] py-1"><span className="text-[var(--mv-dim)]">{tx(l)}</span><span>{tx(t.limits ? usdt(t.limits.limits[k]) : "—")}</span></div>)}
            </div>
            <p className="mt-3 text-xs text-[var(--mv-dim)]">{tx(t.fee_note)}</p>
          </div>
          {tx(e.status !== "claimable" && <p className="flex gap-2 text-sm text-amber-300" data-testid="claim-approval-note"><Info className="h-4 w-4" /> {tx(e.status === "upgrade" ? "You already have a card. This submits an upgrade request; your balance and history stay the same." : e.label)}</p>)}
          <label className="flex items-start gap-2 text-sm text-[var(--mv-muted)]">
            <input type="checkbox" checked={agree} onChange={(ev) => setAgree(ev.target.checked)} className="mt-1" data-testid="claim-agree-checkbox" />
            <span>{tx("I agree to the")} <Link to="/cards/terms" target="_blank" className="text-[var(--mv-teal)]">{tx("MIVO Card Program terms")}</Link>. I understand this is a MIVO-only prepaid USDT card.</span>
          </label>
          <button disabled={!valid || !agree || busy || ["current", "pending", "invite_only"].includes(e.status)} onClick={submit} className="w-full mv-btn-primary rounded-xl py-4 text-sm disabled:opacity-40" data-testid="claim-submit-button">
            {tx(busy ? "Processing…" : e.status === "claimable" ? "Claim Card" : "Submit for approval")}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CardTerms() { useLang();
  return (
    <div className="mv-container max-w-3xl" data-testid="card-terms-page">
      <PageHead eyebrow={tx("MIVO CARD PROGRAM")} title={tx("Card Program terms (draft)")} />
      <p className="mb-4 rounded-lg border border-[var(--mv-border-2)] px-3 py-2 text-xs text-[var(--mv-dim)]" data-testid="legal-english-fallback">{tx("Legal text is shown in English only until a professional translation is approved.")}</p>
      <div className="space-y-4 text-sm leading-relaxed text-[var(--mv-muted)]" data-i18n-skip="legal" lang="en">
        <p className="text-amber-300">DRAFT — pending owner and legal approval. No real funds are accepted yet.</p>
        <p>The MIVO Shopping Card is a MIVO-only prepaid virtual card linked to your single MIVO USDT account. It is not a Visa, Mastercard, bank, debit or credit card, carries no credit line, and cannot be used outside MIVO.</p>
        <p>The MIVO Card Number is an internal identifier. The four-digit MIVO Card Code is not a CVV and never authorizes payments, transfers, password resets or account access on its own.</p>
        <p>Valid Through is a program entitlement date. Expiry or freezing blocks new spending but never forfeits your recorded USDT balance.</p>
        <p>Deposits are credited only after verified on-chain confirmation and reconciliation. Screenshots or proofs alone never create a credit. Deposits exceeding limits are held for review and are never silently destroyed.</p>
        <p>Tier limits are set by MIVO and may change with an effective date. Upgrades change entitlements and limits only — never your balance.</p>
      </div>
    </div>
  );
}
